(ns minorodata.components.dynamic-docs.panel
  (:require ["copy-to-clipboard" :as copy]
            ["lucide-react" :refer [Copy Pencil]]
            [clojure.string :as clojure.string]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.app.utils :as app.utils]
            [minorodata.components.monaco.editor :as monaco.editor]
            [minorodata.components.tables.details :as c.t-details]
            [minorodata.components.transformations.transforms :refer [execution-time]]
            [minorodata.components.transformations.unit-test :refer [get-metadata-params]]
            [minorodata.components.dag.utils :refer [go-to-dag]]
            [minorodata.utils :as minoro.utils]
            [mold-forms.core :as form-component]
            [mold-forms.model :refer [->form-model]]
            [re-posh.core :as re-posh]
            [reagent.core :as reagent]
            [reitit.frontend.easy :as rt.easy]
            [ui.button :refer [button]]
            [ui.chip :refer [chip]]
            [ui.date-time :refer [date-tooltip]]
            [ui.docs-panel :as docs-panel]
            [ui.fields :refer [input]]
            [ui.popover]
            [ui.typography :as typography]
            [ui.utils :refer [clsx]]))


(def summary-links
  [{:title "Details" :href "#details"}
   {:title "Description" :href "#description"}
   {:title "Schedule" :href "#schedule"}
   {:title "Input tables" :href "#input-tables"}
   {:title "Output tables" :href "#output-tables"}
   {:title "SQL" :href "#sql"}
   {:title "Tags" :href "#tags"}])

(defn allowed?
  [permissions]
  @(re-posh/subscribe [:users.subs/has-permissions? permissions]))

(defn fetch-transform
  [entity-id]
  (dispatch [:transform.api/get-latest-version-by-master-id {:master-id entity-id}])
  (dispatch [:connections.api/fetch-transform-statuses {:ids [entity-id]}]))


(defn transform-href
  [transform-id group-id]
  (rt.easy/href :transform-details {:group-id     group-id
                                    :transform-id transform-id}))

(defn set-page-title
  [transform]
  (dispatch [:app.events/set-page-title (str (:transform/name transform) " - "
                                             "Data docs")]))

(defn select-prompt
  []
  [:div {:class "w-full h-full flex justify-center items-center self-center"}
   [:div {:class "text-2xl font-semibold max-w-3xl text-center print:max-w-none"}
    "The information on these pages is updated in real-time and represents the flow of data through Kleene"]])

(defn key-label
  [children]
  [:div {:class "inline-block ml-1 text-[9px] py-0.5 px-1 font-semibold text-white bg-kleene-blue-highlight leading-3 rounded-xs align-middle"}
   children])

(defn docs-header
  [{{:transform/keys [name master-id id group-id]} :transform :keys [transform on-print]}]
  (let [use-master-id? @(re-posh/subscribe [:unleash/is-enabled "transform-execution-dependencies"])
        statuses       @(re-posh/subscribe [:transform-status-list/prop :transform-status-list/statuses])
        filter-fn      (if use-master-id?
                         #(= master-id (:entity-id %))
                         #(= id (:transform-id %)))

        href     (transform-href id group-id)
        status   (minoro.utils/find-first filter-fn statuses)]

    [docs-panel/header
     [docs-panel/title name]
     [docs-panel/description
      [execution-time {:last-run status :class "text-sm"}]]
     [docs-panel/actions {:class "print:hidden"}
      [button {:variant  :empty
               :href     href
               :disabled (and (not (allowed? :connections+transforms/read))
                              (not (allowed? :transforms/read)))}
       "Open in Transforms"]

      [button {:variant  :empty
               :on-click #(go-to-dag transform)
               :disabled (not (allowed? :dag/read))}
       "Open in Pipelines"]

      [button {:variant  :empty
               :on-click on-print}
       "Print Page"]]]))

(defn docs-details
  [{{:transform/keys [updated-by updated-at created-by created-at]} :transform}]
  [:div {:id "details"}
   [docs-panel/section-title "Details"]
   [typography/p "Created "
    [date-tooltip {:date created-at}]
    " by " (:searchable-user/username created-by)]
   [typography/p "Last edited "
    [date-tooltip {:date updated-at}]
    " by " (:searchable-user/username updated-by)]])

(defn prepare-transform
  [transform form-state]
  (-> transform
      app.utils/drop-ns-in-keys
      (update :tags #(map :value %))
      (select-keys [:status :is-temp :is-full :ignore-parser :input-tables :output-tables :command :name :description :tags :schedule-spec])
      (assoc :id          (:transform/id transform)
             :description (-> form-state :form-model/fields first :form-field/value))))

(defn update-description
  [group-id transform on-success form-state]
  (let [transform (prepare-transform transform form-state)]
    (dispatch [:transform.api/update-transform {:group-id   group-id
                                                :transform  transform
                                                :redirect?  false
                                                :on-success on-success}])))

(defn docs-edit-description
  [{:keys [on-cancel transform]
    {:transform/keys [group-id description]} :transform}]
  [:div {}
   [form-component/form
    (->form-model
     {:id        :edit-description
      :on-submit (partial update-description group-id transform on-cancel)
      :clean-up  #(dispatch [:form-model/delete [:form-model/id :edit-description]])
      :fields    [{:name :description :value (or description "")}]})
    (fn [fields errors on-submit]
      [:div {:class "flex flex-col gap-2"}
       [input
        {:field         (:description fields)
         :errors        errors
         :id            :transform-description
         :rows          3
         :textarea      true
         :auto-complete "off"
         :label         "Transform description"}]
       [:footer {:class "flex justify-end gap-2"}
        [button {:on-click on-cancel
                 :variant  :secondary}
         "Cancel"]
        [button {:on-click on-submit}
         "Save"]]])]])

(defn docs-description
  []
  (let [edit-mode? (reagent/atom false)]
    (fn [{:keys [transform]
          {:transform/keys [description]} :transform}]
      [:div {:id "description"}
       [docs-panel/section-title
        [:span {:class "flex gap-2 items-center"}
         "Description"
         (when (allowed? :dynamic-docs/write)
           [button {:size     :icon
                    :variant  :empty
                    :icon     Pencil
                    :on-click #(reset! edit-mode? true)}])]]

       (if @edit-mode?
         [docs-edit-description {:transform transform
                                 :on-cancel #(reset! edit-mode? false)}]
         [typography/p {:class "whitespace-pre-wrap"}
          (or description
              [:span {:class "text-kleene-text-secondary"}
               "No description available, please add one in the Transform description. This will help other users understand what the purpose of this Transform is."])])])))

(defn docs-schedule
  [{{:transform/keys [id] :as transform} :transform}]
  (let [upstream-deps? @(re-posh/subscribe [:dag.subs/connected-nodes? id])
        timezone       @(re-posh/subscribe [:app.subs/timezone])]
    [:div {:id "schedule"}
     [docs-panel/section-title
      "Schedule (" timezone " time zone)"]
     [typography/p
      (app.utils/transform-schedule
       transform
       (when upstream-deps? "Dependency based"))]]))

(defn docs-input-tables
  [{{:transform/keys [input-tables]} :transform}]
  [:div {:id "input-tables"}
   [docs-panel/section-title
    "Input tables"]
   (if (empty? input-tables)
     [typography/p {:class "text-kleene-text-secondary"}
      "The transform does not have any input tables configured"]
     [:ul {:class "list-disc list-inside"}
      (for [it input-tables]
        ^{:key it}
        [:li it])])])


(defn generate-table-info-link [{:keys [catalog schema table]}]
  (rt.easy/href :table-details {} {:db catalog :schema schema :table table}))

(defn open-table-info-modal [{:keys [catalog schema table]}]
  (fn [e]
    (.preventDefault e)
    (c.t-details/open-table-info-modal {:db catalog :schema schema :table table})))

(defn output-table
  [{:keys [table]}]
  (let [params  (get-metadata-params table)
        columns @(re-posh/subscribe [:sql-metadata-partial/prop [:sql-metadata-partial/params params]
                                     :sql-metadata-partial/metadata])

        show-table-details? @(re-posh/subscribe [:app.subs/enabled-for-bigquery :tables])]
    [docs-panel/details
     [(if show-table-details?
        :a
        :span)

      (if show-table-details?
        {:target   "_blank"
         :class    "flex transition-opacity hover:opacity-75"
         :href     (generate-table-info-link params)
         :on-click (open-table-info-modal params)}
        {:class "flex"})
      [docs-panel/details-title table]]
     (when (not-empty columns)
       [docs-panel/details-content
        [:table {:class "text-sm leading-6"}
         [:thead {:class "font-bold"}
          [:tr
           [:th {:scope "col"
                 :class "w-60 text-left py-1"}
            "Column"]
           [:th {:scope "col"
                 :class "text-left py-1"}
            "Type"]]]
         [:tbody
          (for [col columns]
            ^{:key (:column-name col)}
            [:tr
             [:td {:class "py-1"}
              (:column-name col)
              (when (contains? col :pk-name) [key-label "PK"])
              (when (contains? col :fk-name) [key-label "FK"])]
             [:td {:class "py-1"}
              (:type-name col)]])]]])]))


(defn docs-output-tables
  [{{:transform/keys [output-tables]} :transform}]
  (let [_ (when (not-empty output-tables)
            (doall
             (for [table-name output-tables
                   :let [params (get-metadata-params table-name)]]
               (dispatch [:sql-console.api/fetch-metadata params]))))]
    (fn [{{:transform/keys [output-tables]} :transform}]
      [:div {:id "output-tables"}
       [docs-panel/section-title
        "Output tables"]
       (if (empty? output-tables)
         [typography/p {:class "text-kleene-text-secondary"}
          "The transform does not have any output tables configured"]
         [:div {:class "flex flex-col gap-4"}
          (for [table output-tables]
            ^{:key table}
            [output-table {:table table}])])])))

(defn copy-button [{:keys [command]}]
  (let [open-tooltip (reagent/atom false)]
    (fn []
      [:div {:class "absolute top-0 -right-2 translate-x-full print:hidden"}
       [ui.popover/popover {:content  [:span "Copied into clipboard!"]
                            :placement :top
                            :open @open-tooltip}
        [:div
         [button {:variant  :empty
                  :size     :icon-md
                  :icon     Copy
                  :on-click (fn []
                              (copy command #js {:format "text/plain"})
                              (reset! open-tooltip true)
                              (js/setTimeout #(reset! open-tooltip false) 2500))}]]]])))

(defn get-code-height [command]
  (some-> command
          (clojure.string/split "\n")
          count
          (* 19)))

(defn docs-sql []
  (let [;; To highlight syntax in print mode
        _ (some-> (js/document.querySelector "style.monaco-colors")
                  (.removeAttribute "media"))]
    (fn [{:keys [printing?]
          {:transform/keys [command]} :transform}]
      (let [dark-mode?    @(re-posh/subscribe [:app/prop :app/dark-mode?])
            editor-height (if printing?
                            (get-code-height command)
                            384)]
        [:div {:id "sql"}
         [docs-panel/section-title
          "SQL"]
         [:div {:class (str "dynamic-docs-editor-wrapper relative")
                :style {"--editor-height" (str editor-height "px")
                        :height editor-height}}
          [:f> monaco.editor/editor
           {:theme     (if dark-mode? "vs-dark" "light")
            :data-cy   "dynamic-docs-editor"
            :options   {:fontSize        14
                        :automaticLayout true
                        :contextmenu     true
                        :readOnly        true
                        :wordWrap        true
                        :minimap         {:enabled false}}
            :value     command}]

          [copy-button {:command command}]]]))))

(defn docs-tags
  [{{:transform/keys [tags]} :transform}]
  [:div {:id "tags"}
   [docs-panel/section-title
    "Tags"]
   [:div {:class "flex flex-wrap gap-1"}
    (if (empty? tags)
      [typography/p {:class "text-kleene-text-secondary"} "The transform does not have any tags added"]
      (for [{:transform-tag/keys [value]} tags]
        ^{:key value}
        [chip value]))]])

(defn docs-summary-link
  [props children]
  [:a (assoc props
             :on-click #(.scrollIntoView (js/document.querySelector (:href props)) #js {:behavior "smooth"})
             :class (clsx "text-kleene-text hover:text-kleene-text/60 transition-colors"
                          (:class props)))
   children])

(defn docs-summary
  []
  [:div {:class "bg-kleene-background-secondary px-4 py-3 flex flex-col text-sm gap-1 rounded-xs"}
   (for [{:keys [title href]} summary-links]
     ^{:key href}
     [docs-summary-link {:href href} title])])

(defn printing-handler []
  (let [printing?     (reagent/atom false)

        on-print      (fn []
                        (reset! printing? true)
                        (js/setTimeout
                         (fn []
                           (js/window.print)
                           (reset! printing? false))
                         500))]
    [printing? on-print]))

(defn transform-docs
  [{:keys [transform]}]
  (let [[printing? on-print] (printing-handler)

        _ (set-page-title transform)]
    (fn [{:keys [transform]}]
      [:div {:class "flex justify-between"}
       [docs-panel/panel {:class "max-w-3xl print:max-w-none print:p-0"}
        [docs-header {:transform transform
                      :on-print  on-print}]

        [docs-panel/content
         [docs-details {:transform transform}]
         [docs-description {:transform transform}]
         [docs-schedule {:transform transform}]
         [docs-input-tables {:transform transform}]
         [docs-output-tables {:transform transform}]
         [docs-sql {:transform transform
                    :printing? @printing?}]
         [docs-tags {:transform transform}]]]

       [:div {:class "relative z-20 shrink-0 mr-6 print:hidden"}
        [:div {:class "sticky top-10"}
         [docs-summary]]]])))

(defn panel-inner []
  (let [last-id (atom nil)
        fetch-if-needed (fn [id]
                          (when (not= @last-id id)
                            (fetch-transform id)
                            (reset! last-id id)))]
    (reagent/create-class
     {:display-name "panel"

      :component-did-mount
      (fn [this]
        (let [props (reagent/props this)]
          (fetch-if-needed (:entity-id props))))

      :component-did-update
      (fn [this]
        (let [props (reagent/props this)]
          (fetch-if-needed (:entity-id props))))

      :render
      (fn [this]
        (let [props (reagent/props this)
              {:keys [entity-id]} props

              {:transform-master/keys [transform]}
              @(re-posh/subscribe [:transform-master/pull
                                   [:transform-master/master-id entity-id]
                                   '[* {:transform-master/transform [* {:transform/updated-by [:searchable-user/username]
                                                                        :transform/created-by [:searchable-user/username]
                                                                        :transform/tags       [:transform-tag/value]}]}]])]
          (if transform
            [transform-docs {:transform transform}]
            [select-prompt])))})))

(defn panel []
  (let [{:keys [entity-id]} @(re-posh/subscribe [:app-route/prop :app-route/params])]
    [panel-inner {:entity-id entity-id}]))
